import {
  Button,
  Card,
  formatDate,
  Grid,
  markedForDeletionStyle,
  MergeInfoBlock,
  pn,
  Stack,
  Typography,
  useTheme,
} from "@compass/core";
import { StudentBulkMerge, StudentBulkMergeResource, StudentBulkMergeStudent } from "generated/openApi";

export const Students = (props: {
  students: StudentBulkMergeStudent[];
  resource: StudentBulkMergeResource;
  mergeToStudentId: number | undefined;
  setMergeStudentId: (id: number) => void;
}) => {
  const properties = props.resource.studentProperties || [];
  const theme = useTheme();

  const getStudentsCount = (
    students: StudentBulkMerge["students"],
    countKey: keyof StudentBulkMergeStudent,
    currentStudent: StudentBulkMergeStudent
  ): number => {
    if (!students) {
      return 0;
    }

    return students.reduce((total: number, student) => {
      if (student.id !== currentStudent.id) {
        // @ts-ignore
        total += student[countKey] || 0;
      }
      return total;
    }, 0);
  };

  return (
    <Stack direction="column" spacing="md">
      <Typography variant="caption">
        Select a student to keep. The rest of the students will be deleted and their data will be transferred to the
        keeping student.
      </Typography>

      {props.students.map((student) => {
        const keeping = props.mergeToStudentId === student.id;

        const markedForDelete = props.mergeToStudentId !== undefined && props.mergeToStudentId > 0 && !keeping;
        const removingSx = markedForDelete ? markedForDeletionStyle(theme) : undefined;

        return (
          <Card
            header={
              <Stack direction="row" alignItems={"center"} spacing="md">
                <Button
                  variant={keeping ? "contained" : "outlined"}
                  onClick={() => props.setMergeStudentId(student.id)}
                >
                  {keeping ? "Keeping" : "Keep"}
                </Button>

                <Stack>
                  <Typography>
                    {student.lastName}, {student.firstName}
                  </Typography>

                  <Stack direction="row" spacing="sm">
                    <Typography variant="caption">{student.customId1}</Typography>
                    <Typography variant="caption">{formatDate(student.dateOfBirth)}</Typography>
                  </Stack>
                </Stack>
              </Stack>
            }
          >
            <Grid container spacing="lg" sx={{ ...removingSx }}>
              <Grid item>
                <MergeInfoBlock
                  value={student.enrollmentsCount}
                  label={
                    properties.find((x) => x.propertyName === pn<StudentBulkMergeStudent>("enrollmentsCount"))
                      ?.displayName
                  }
                  addCount={
                    props.mergeToStudentId === student.id
                      ? getStudentsCount(props.students, "enrollmentsCount", student)
                      : undefined
                  }
                />
              </Grid>
              <Grid item>
                <MergeInfoBlock
                  value={student.servicesCount}
                  label={
                    properties.find((x) => x.propertyName === pn<StudentBulkMergeStudent>("servicesCount"))?.displayName
                  }
                  addCount={
                    props.mergeToStudentId === student.id
                      ? getStudentsCount(props.students, "servicesCount", student)
                      : undefined
                  }
                />
              </Grid>
              <Grid item>
                <MergeInfoBlock
                  value={student.parentsCount}
                  label={
                    properties.find((x) => x.propertyName === pn<StudentBulkMergeStudent>("parentsCount"))?.displayName
                  }
                  addCount={
                    props.mergeToStudentId === student.id
                      ? getStudentsCount(props.students, "parentsCount", student)
                      : undefined
                  }
                />
              </Grid>
              <Grid item>
                <MergeInfoBlock
                  value={student.gradesCount}
                  label={
                    properties.find((x) => x.propertyName === pn<StudentBulkMergeStudent>("gradesCount"))?.displayName
                  }
                  addCount={
                    props.mergeToStudentId === student.id
                      ? getStudentsCount(props.students, "gradesCount", student)
                      : undefined
                  }
                />
              </Grid>
              <Grid item>
                <MergeInfoBlock
                  value={student.absencesCount}
                  label={
                    properties.find((x) => x.propertyName === pn<StudentBulkMergeStudent>("absencesCount"))?.displayName
                  }
                  addCount={
                    props.mergeToStudentId === student.id
                      ? getStudentsCount(props.students, "absencesCount", student)
                      : undefined
                  }
                />
              </Grid>
              <Grid item>
                <MergeInfoBlock
                  value={student.testsCount}
                  label={
                    properties.find((x) => x.propertyName === pn<StudentBulkMergeStudent>("testsCount"))?.displayName
                  }
                  addCount={
                    props.mergeToStudentId === student.id
                      ? getStudentsCount(props.students, "testsCount", student)
                      : undefined
                  }
                />
              </Grid>
              <Grid item>
                <MergeInfoBlock
                  value={student.gpaCount}
                  label={
                    properties.find((x) => x.propertyName === pn<StudentBulkMergeStudent>("gpaCount"))?.displayName
                  }
                  addCount={
                    props.mergeToStudentId === student.id
                      ? getStudentsCount(props.students, "gpaCount", student)
                      : undefined
                  }
                />
              </Grid>
              <Grid item>
                <MergeInfoBlock
                  value={student.diplomasCount}
                  label={
                    properties.find((x) => x.propertyName === pn<StudentBulkMergeStudent>("diplomasCount"))?.displayName
                  }
                  addCount={
                    props.mergeToStudentId === student.id
                      ? getStudentsCount(props.students, "diplomasCount", student)
                      : undefined
                  }
                />
              </Grid>
              <Grid item>
                <MergeInfoBlock
                  value={student.postHighSchoolsCount}
                  label={
                    properties.find((x) => x.propertyName === pn<StudentBulkMergeStudent>("postHighSchoolsCount"))
                      ?.displayName
                  }
                  addCount={
                    props.mergeToStudentId === student.id
                      ? getStudentsCount(props.students, "postHighSchoolsCount", student)
                      : undefined
                  }
                />
              </Grid>
              <Grid item>
                <MergeInfoBlock
                  value={student.fafsasCount}
                  label={
                    properties.find((x) => x.propertyName === pn<StudentBulkMergeStudent>("fafsasCount"))?.displayName
                  }
                  addCount={
                    props.mergeToStudentId === student.id
                      ? getStudentsCount(props.students, "fafsasCount", student)
                      : undefined
                  }
                />
              </Grid>
              <Grid item>
                <MergeInfoBlock
                  value={student.scholarshipsCount}
                  label={
                    properties.find((x) => x.propertyName === pn<StudentBulkMergeStudent>("scholarshipsCount"))
                      ?.displayName
                  }
                  addCount={
                    props.mergeToStudentId === student.id
                      ? getStudentsCount(props.students, "scholarshipsCount", student)
                      : undefined
                  }
                />
              </Grid>

              <Grid item>
                <MergeInfoBlock
                  value={student.applicationsCount}
                  label={
                    properties.find((x) => x.propertyName === pn<StudentBulkMergeStudent>("applicationsCount"))
                      ?.displayName
                  }
                  addCount={
                    props.mergeToStudentId === student.id
                      ? getStudentsCount(props.students, "applicationsCount", student)
                      : undefined
                  }
                />
              </Grid>
              
              <Grid item>
                <MergeInfoBlock
                  value={student.filesCount}
                  label={
                    properties.find((x) => x.propertyName === pn<StudentBulkMergeStudent>("filesCount"))?.displayName
                  }
                  addCount={
                    props.mergeToStudentId === student.id
                      ? getStudentsCount(props.students, "filesCount", student)
                      : undefined
                  }
                />
              </Grid>
            </Grid>
          </Card>
        );
      })}
    </Stack>
  );
};
